import { useState } from 'react';
import { FiX, FiLoader, FiCopy, FiCheck } from 'react-icons/fi';

interface AIAssistantProps {
  selectedText: string;
  action: 'ask' | 'summarize' | 'translate' | 'explain';
  onClose: () => void;
}

const AIAssistant = ({ selectedText, action, onClose }: AIAssistantProps) => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const actionLabels = {
    ask: '💬 AI에게 질문하기',
    summarize: '📝 요약하기',
    translate: '🌐 번역하기',
    explain: '💡 설명하기',
  };

  const handleAskAI = async () => {
    setLoading(true);
    setError('');
    setResponse('');

    try {
      const res = await fetch('/api/ai/assist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: selectedText,
          action,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setResponse(data.response);
      } else {
        setError(data.error || 'AI 처리 중 오류가 발생했습니다.');
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Auto-fetch on mount
  useState(() => {
    handleAskAI();
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full shadow-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {actionLabels[action]}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Selected Text */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">선택한 텍스트:</p>
          <p className="text-sm text-gray-900 dark:text-white italic max-h-24 overflow-y-auto">
            "{selectedText}"
          </p>
        </div>

        {/* Response */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <FiLoader size={32} className="animate-spin text-primary mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                AI가 분석 중입니다...
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-200">
                ⚠️ {error}
              </p>
            </div>
          )}

          {response && (
            <div>
              <div className="prose dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-gray-900 dark:text-white">
                  {response}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {response && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
              <span>{copied ? '복사됨!' : '복사'}</span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 gradient-primary text-white rounded-lg hover:shadow-lg transition-all"
            >
              닫기
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
